import { useCallback, useEffect, useState } from "react";
import { fetchDriveContents, type GraphDriveItem } from "../../../email/services/graph";
import { useAuthState } from "../../../../auth/AuthStateProvider";
import { logConnectorFailure, logConnectorSuccess } from "../../../../telemetry";

/**
 * The folder browser drives both OneDrive (`/me/drive`) and SharePoint document
 * libraries (`/sites/{id}/drive`, `/drives/{driveId}`). Tag the failure with the
 * matching connector so it lands in the right pivot.
 */
function connectorFor(drivePath: string | null): string {
  return drivePath?.startsWith("/me/") ? "onedrive" : "sharepoint";
}

export interface BreadcrumbEntry {
  /** `null` represents the drive root; non-null is a folder item id. */
  id: string | null;
  name: string;
}

interface UseFolderBrowserResult {
  items: GraphDriveItem[];
  loading: boolean;
  error: string | null;
  path: BreadcrumbEntry[];
  navigateInto: (folder: GraphDriveItem) => void;
  navigateTo: (index: number) => void;
}

/**
 * Folder browser state for any Graph drive (OneDrive or SharePoint site library).
 *
 * Pass `drivePath = null` to keep the hook idle (e.g. while no SharePoint site
 * is selected). When `drivePath`, `rootLabel`, or `rootParentId` changes, the
 * path resets to the root.
 *
 * `rootParentId` chooses where the browse starts: `null` (default) is the drive
 * root; a folder item id starts the browse inside that folder — used to open a
 * folder picked from cross-tenant search results directly at its contents.
 */
export function useFolderBrowser(
  token: string | null,
  drivePath: string | null,
  rootLabel: string,
  rootParentId: string | null = null,
): UseFolderBrowserResult {
  const { refreshGraphToken } = useAuthState();
  const [path, setPath] = useState<BreadcrumbEntry[]>([{ id: rootParentId, name: rootLabel }]);
  const [items, setItems] = useState<GraphDriveItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset path when the drive (or the starting folder) changes.
  useEffect(() => {
    setPath([{ id: rootParentId, name: rootLabel }]);
  }, [drivePath, rootLabel, rootParentId]);

  const currentParent = path[path.length - 1]?.id ?? null;

  useEffect(() => {
    if (!token || !drivePath) {
      setItems([]);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchDriveContents(token, drivePath, currentParent, refreshGraphToken)
      .then((result) => {
        if (cancelled) return;
        setItems(result);
        logConnectorSuccess(connectorFor(drivePath), "browse-folder");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError("Couldn't load files. Try signing in again.");
        logConnectorFailure(connectorFor(drivePath), e, "browse-folder");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, drivePath, currentParent, refreshGraphToken]);

  const navigateInto = useCallback((folder: GraphDriveItem) => {
    setPath((prev) => [...prev, { id: folder.id, name: folder.name || "Folder" }]);
  }, []);

  const navigateTo = useCallback((index: number) => {
    setPath((prev) => (index >= 0 && index < prev.length ? prev.slice(0, index + 1) : prev));
  }, []);

  return { items, loading, error, path, navigateInto, navigateTo };
}
